import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Checkbox, Modal, Space, Spin, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import * as api from '@apis/grouping-agent';
import { GroupingAgentGroup } from '@models/grouping-agent';
import EnrollmentSourceDrawer from './EnrollmentSourceDrawer';
import GroupEnrollmentMembersTable from './GroupEnrollmentMembersTable';

const { Text } = Typography;

interface Props {
  runId: string;
  group: GroupingAgentGroup | null;
  open: boolean;
  onClose: () => void;
}

const GroupEnrollmentsModal: React.FC<Props> = ({ runId, group, open, onClose }) => {
  const [namePendingOnly, setNamePendingOnly] = useState(false);
  const [sourceScheduleId, setSourceScheduleId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['grouping-agent-group-enrollments', runId, group?._id],
    queryFn: () => api.getGroupingAgentGroupEnrollments(runId, group!._id),
    enabled: open && !!group?._id,
  });

  const data = query.data?.data;
  const members = useMemo(() => {
    const list = data?.members || [];
    if (!namePendingOnly) return list;
    return list.filter((m) => m.namePending);
  }, [data?.members, namePendingOnly]);

  const title = group ? `${group.productName || ''} — ${group.optionName || group.name}` : '그룹 수강생';

  return (
    <>
      <Modal
        title={title}
        open={open}
        onCancel={onClose}
        footer={null}
        width={1000}
        destroyOnClose
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}>
        {query.isLoading && (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin />
          </div>
        )}

        {data && !query.isLoading && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space wrap>
              <Text>
                {data.group.name} · {data.group.currentCount}/{data.group.studentsMin}/
                {data.group.studentsMax < 0 ? '제한없음' : data.group.studentsMax}
              </Text>
              <Tag color={data.group.groupStatus === 'short' ? 'red' : 'green'}>{data.group.groupStatus}</Tag>
              {(data.group.namePendingCount ?? 0) > 0 && (
                <Badge count={data.group.namePendingCount} color="orange" text="이름 보충" />
              )}
              {data.group.startDate && (
                <Text type="secondary">{dayjs(data.group.startDate).format('YYYY-MM-DD HH:mm')}</Text>
              )}
            </Space>

            <Checkbox checked={namePendingOnly} onChange={(e) => setNamePendingOnly(e.target.checked)}>
              이름 보충만 보기
            </Checkbox>

            <GroupEnrollmentMembersTable
              members={members}
              onOpenSource={(scheduleId) => setSourceScheduleId(scheduleId)}
            />
          </Space>
        )}
      </Modal>

      <EnrollmentSourceDrawer
        scheduleId={sourceScheduleId}
        open={!!sourceScheduleId}
        onClose={() => setSourceScheduleId(null)}
      />
    </>
  );
};

export default GroupEnrollmentsModal;
